import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { isCustomerRole } from '@itr/shared';
import {
  Activity,
  ExternalLink,
  Globe,
  Network,
  Play,
  Radar,
  Route,
  Search,
  Server,
  Square,
  Terminal,
  Wifi,
} from 'lucide-react';
import { api, apiUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, EmptyState, useFetch, useToast } from '../components/ui';
import {
  DiagnosticProgress,
  DiagnosticResults,
  STREAMING_TOOLS,
  ToolOptions,
  useDiagnosticRun,
  usePingTransitionAlerts,
  type DiagnosticTool,
} from '../components/diagnostics';

type ToolId = DiagnosticTool | 'neighbours' | 'proxy';

const TOOLS: Array<{ id: ToolId; label: string; icon: typeof Activity; permission: string; blurb: string }> = [
  { id: 'ping', label: 'Ping', icon: Activity, permission: 'tools:run', blurb: 'Reachability, latency and jitter.' },
  { id: 'traceroute', label: 'Traceroute', icon: Route, permission: 'tools:run', blurb: 'The path packets take, hop by hop.' },
  { id: 'port-scan', label: 'Port scan', icon: Radar, permission: 'tools:scan', blurb: 'Which TCP services are listening.' },
  { id: 'sweep', label: 'Neighbour sweep', icon: Network, permission: 'tools:scan', blurb: 'Find live hosts on a subnet.' },
  { id: 'neighbours', label: 'Neighbours', icon: Wifi, permission: 'tools:run', blurb: "This server's ARP/NDP table." },
  { id: 'lldp', label: 'LLDP', icon: Server, permission: 'tools:run', blurb: 'What a switch says is on each port.' },
  { id: 'dns', label: 'DNS', icon: Search, permission: 'tools:run', blurb: 'Resolve records, optionally via a chosen server.' },
  { id: 'http', label: 'HTTP', icon: Globe, permission: 'tools:run', blurb: 'Status, timing, headers and redirects.' },
  { id: 'proxy', label: 'Web proxy', icon: ExternalLink, permission: 'tools:proxy', blurb: "Open a device's admin UI through the server." },
];

export function ToolsPage() {
  const { can, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tool, setTool] = useState<ToolId>((params.get('tool') as ToolId) ?? 'ping');

  const available = TOOLS.filter((t) => can(t.permission as never));
  const active = available.find((t) => t.id === tool) ?? available[0];

  useEffect(() => {
    if (active && active.id !== tool) setTool(active.id);
  }, [active, tool]);

  // Every tool here targets an address the operator types in, not a
  // registered resource — the one shape of diagnostic a client-access role
  // can't reach (see assertNotFreeTargetClient in tools-routes.ts). The
  // equivalent, resource-scoped diagnostics live on each resource's own page.
  if (isCustomerRole(user?.role)) return <Navigate to="/" replace />;

  if (available.length === 0) {
    return <EmptyState icon={<Terminal size={20} />} title="No diagnostics available" text="Your role does not include any diagnostic permissions." />;
  }

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Diagnostics</h1>
          <div className="page-subtitle">{active?.blurb}</div>
        </div>
      </div>

      <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {available.map((t) => (
          <button
            key={t.id}
            className={`btn btn-sm${tool === t.id ? ' btn-primary' : ''}`}
            onClick={() => {
              setTool(t.id);
              setParams({ tool: t.id }, { replace: true });
            }}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tool === 'neighbours' ? <NeighbourTable /> : tool === 'proxy' ? <ProxyPanel /> : <RunnerPanel tool={tool} />}
    </>
  );
}

/* ================================================================== */
/* Streaming + one-shot runner                                        */
/* ================================================================== */

function RunnerPanel({ tool }: { tool: DiagnosticTool }) {
  const [target, setTarget] = useState('');
  const [options, setOptions] = useState<Record<string, unknown>>({});
  const run = useDiagnosticRun();
  const { reset } = run;

  usePingTransitionAlerts(run, tool === 'ping' && Boolean(options.continuous), target.trim() || 'Target');

  // Reset everything when switching tools — stale hops under a port-scan header
  // would be worse than an empty panel.
  useEffect(() => {
    reset();
    setOptions({});
  }, [tool, reset]);

  const start = () => {
    if (!target.trim()) return;
    const body = tool === 'sweep' ? { cidr: target.trim(), ...options } : { target: target.trim(), ...options };
    void run.start(`/api/tools/${tool}`, body, STREAMING_TOOLS.includes(tool));
  };

  const placeholder =
    tool === 'sweep' ? '10.0.1.0/24' : tool === 'http' ? 'https://10.0.0.1/' : tool === 'dns' ? 'example.internal' : '10.0.0.1 or host.internal';

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body">
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 280px' }}>
              <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
                {tool === 'sweep' ? 'Subnet (CIDR)' : tool === 'http' ? 'URL' : 'Target'}
              </label>
              <input
                className="input"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !run.running) start();
                }}
                placeholder={placeholder}
                autoFocus
                spellCheck={false}
              />
            </div>

            <ToolOptions tool={tool} options={options} onChange={setOptions} />

            {run.running ? (
              <button className="btn btn-danger" onClick={run.stop}>
                <Square size={14} /> Stop
              </button>
            ) : (
              <button className="btn btn-primary" onClick={start} disabled={!target.trim()}>
                <Play size={14} /> Run
              </button>
            )}
          </div>

          <DiagnosticProgress run={run} />
        </div>
      </div>

      <DiagnosticResults tool={tool} run={run} />
    </>
  );
}

/* ================================================================== */
/* Neighbours                                                         */
/* ================================================================== */

function NeighbourTable() {
  const { data, loading, reload } = useFetch(
    () =>
      api.get<{
        neighbours: Array<{ ip: string; mac: string; vendor: string | null; iface: string | null; state: string | null }>;
        subnets: Array<{ iface: string; address: string; cidr: string; mac: string }>;
      }>('/api/tools/neighbours'),
    [],
  );
  const [filter, setFilter] = useState('');

  const neighbours = (data?.neighbours ?? []).filter((n) => {
    const needle = filter.toLowerCase();
    return !needle || n.ip.includes(needle) || n.mac.toLowerCase().includes(needle) || (n.vendor ?? '').toLowerCase().includes(needle);
  });

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <Network size={14} />
          <span className="card-title">Directly connected networks</span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Interface</th>
                <th>Address</th>
                <th>Subnet</th>
                <th>MAC</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {(data?.subnets ?? []).map((s) => (
                <tr key={s.iface + s.address}>
                  <td>{s.iface}</td>
                  <td className="mono">{s.address}</td>
                  <td className="mono subtle">{s.cidr}</td>
                  <td className="mono subtle">{s.mac}</td>
                  <td>
                    {/* One click from "here is a subnet" to "sweep it" — the
                        step an engineer takes every single time. */}
                    <a className="btn btn-sm" href={`/tools?tool=sweep&cidr=${encodeURIComponent(s.cidr)}`}>
                      <Radar size={12} /> Sweep
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <Wifi size={14} />
          <span className="card-title">Neighbour table</span>
          <span className="grow" />
          <input
            className="input"
            style={{ width: 200, height: 28 }}
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="btn btn-sm" onClick={reload}>Refresh</button>
        </div>
        {loading ? (
          <div style={{ padding: 16 }}><span className="spinner" /></div>
        ) : neighbours.length === 0 ? (
          <EmptyState title="No neighbours" text="Nothing in the server's ARP/NDP table yet. Run a sweep to populate it." />
        ) : (
          <div className="table-wrap" style={{ maxHeight: 520 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>MAC</th>
                  <th>Vendor</th>
                  <th>Interface</th>
                  <th style={{ width: 100 }}>State</th>
                </tr>
              </thead>
              <tbody>
                {neighbours.map((n) => (
                  <tr key={n.ip + n.mac}>
                    <td className="mono">{n.ip}</td>
                    <td className="mono subtle">{n.mac}</td>
                    <td>{n.vendor ?? <span className="subtle">—</span>}</td>
                    <td className="subtle">{n.iface ?? '—'}</td>
                    <td>{n.state ? <Badge>{n.state}</Badge> : <span className="subtle">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ================================================================== */
/* Web proxy                                                          */
/* ================================================================== */

function ProxyPanel() {
  const { data, loading } = useFetch(
    () => api.get<Array<{ id: string; name: string; kind: string; health: string; url: string }>>('/api/tools/proxy/targets'),
    [],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const toast = useToast();

  const targets = data ?? [];
  const openTarget = targets.find((t) => t.id === openId);

  // A bare link to the proxy path carries no credential, so every open starts
  // with a session — which, with a separate proxy origin, is also what carries
  // the operator over to it.
  const proxySessionUrl = async (id: string): Promise<string> => {
    const { url } = await api.post<{ url: string }>(`/api/tools/proxy/${id}/session`, {});
    return /^https?:\/\//.test(url) ? url : apiUrl(url);
  };

  const openHere = async (id: string) => {
    if (id === openId) {
      setOpenId(null);
      setFrameUrl(null);
      return;
    }
    try {
      setFrameUrl(await proxySessionUrl(id));
      setOpenId(id);
    } catch (err) {
      toast('Could not open', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const openInNewTab = async (id: string) => {
    const win = window.open('', '_blank');
    try {
      const url = await proxySessionUrl(id);
      if (win) win.location.href = url;
      else toast('Pop-up blocked', 'bad', 'Allow pop-ups for this site to open the device UI in a new tab.');
    } catch (err) {
      win?.close();
      toast('Could not open', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ fontSize: 13, lineHeight: 1.6 }}>
          <p className="subtle">
            Reach a monitored device's own web interface through this server — useful when the device sits on a customer
            LAN you cannot route to directly. Only registered resources can be opened; there is deliberately no
            free-form URL box, because that would make this server an open relay into every network it can see.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card"><div style={{ padding: 16 }}><span className="spinner" /></div></div>
      ) : targets.length === 0 ? (
        <EmptyState
          icon={<ExternalLink size={20} />}
          title="No proxyable devices"
          text="No resource has an admin URL or reachable address configured yet."
        />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th style={{ width: 130 }}>Kind</th>
                  <th>Address</th>
                  <th style={{ width: 190 }} />
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 550 }}>{t.name}</td>
                    <td><Badge>{t.kind}</Badge></td>
                    <td className="mono subtle truncate" style={{ maxWidth: 280 }}>{t.url}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button className="btn btn-sm" onClick={() => void openHere(t.id)}>
                          {t.id === openId ? 'Hide' : 'Open here'}
                        </button>
                        <button className="btn btn-sm" onClick={() => void openInNewTab(t.id)}>
                          <ExternalLink size={12} /> New tab
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openTarget && frameUrl && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-header">
            <ExternalLink size={14} />
            <span className="card-title">{openTarget.name}</span>
            <span className="card-subtitle">{openTarget.url}</span>
            <span className="grow" />
            <button
              className="btn btn-sm"
              onClick={() => {
                setOpenId(null);
                setFrameUrl(null);
              }}
            >
              Close
            </button>
          </div>
          <iframe
            title={openTarget.name}
            src={frameUrl}
            style={{ width: '100%', height: 620, border: 'none', background: '#fff' }}
            // The device's own pages are untrusted third-party content. With a
            // separate proxy origin, `allow-same-origin` means the proxy's own
            // origin (its cookies, its paths), never the portal's.
            sandbox="allow-forms allow-scripts allow-same-origin"
          />
        </div>
      )}
    </>
  );
}
